import { useState } from 'react'
import { Plus, Send, Pencil, Trash2, Sparkles, Target, Clock4, LayoutList } from 'lucide-react'
import { Modal, ConfirmModal } from '../common/Modal'

const NOTIFS = [
  { id: 'N-001', title: 'New Feature: Analytics Dashboard', audience: 'All Users', status: 'sent', sent: 'May 3, 2025', reach: '24,892' },
  { id: 'N-002', title: 'Scheduled Maintenance — May 10', audience: 'All Users', status: 'sent', sent: 'May 1, 2025', reach: '24,892' },
  { id: 'N-003', title: 'Enterprise Plan Upgrade Offer', audience: 'Pro Users', status: 'scheduled', sent: 'May 6, 2025', reach: '4,880' },
  { id: 'N-004', title: 'Welcome to Admin Panel v2.0', audience: 'New Users', status: 'draft', sent: '—', reach: '—' },
]
const STATUS = {
  sent: { cls: 'badge-active', l: 'Sent' },
  scheduled: { cls: 'badge-pending', l: 'Scheduled' },
  draft: { cls: 'badge-warning', l: 'Draft' },
}

export default function PushNotification() {
  const [notifs, setNotifs] = useState(NOTIFS)
  const [modalOpen, setModalOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [form, setForm] = useState({ title: '', audience: 'All Users', message: '', status: 'draft', reach: '—' })

  const openAdd = () => {
    setActive(null)
    setForm({ title: '', audience: 'All Users', message: '', status: 'draft', reach: '—' })
    setModalOpen(true)
  }

  const openEdit = (n) => {
    setActive(n)
    setForm({ title: n.title, audience: n.audience, message: n.message || '', status: n.status, reach: n.reach })
    setModalOpen(true)
  }

  const save = () => {
    if (!form.title.trim()) return
    const payload = {
      title: form.title, audience: form.audience, status: form.status,
      reach: form.reach || '—', message: form.message,
      sent: form.status === 'sent' ? 'Today' : form.status === 'scheduled' ? 'Scheduled' : '—',
    }
    if (active) {
      setNotifs(prev => prev.map(n => n.id === active.id ? { ...n, ...payload } : n))
    } else {
      const id = `N-${String(notifs.length + 1).padStart(3, '0')}`
      setNotifs(prev => [{ id, ...payload }, ...prev])
    }
    setModalOpen(false)
  }

  const onDelete = () => {
    setNotifs(prev => prev.filter(n => n.id !== active.id))
    setDeleteOpen(false)
    setActive(null)
  }

  const sentCount = notifs.filter(n => n.status === 'sent').length
  const scheduledCount = notifs.filter(n => n.status === 'scheduled').length
  const draftCount = notifs.filter(n => n.status === 'draft').length

  const stats = [
    { label: 'Total Sent', value: sentCount,variant: 'indigo', icon: Sparkles,  color: '#4F46E5', colorGlow: 'rgba(79,70,229,0.18)', trend: `${sentCount}`,trendColor: '#4F46E5', bar: Math.round(sentCount / notifs.length * 100) },
    { label: 'Scheduled', value: scheduledCount,variant: 'amber', icon: Clock4,  color: '#F59E0B', colorGlow: 'rgba(245,158,11,0.15)', trend: `${scheduledCount}`, bar: Math.round(scheduledCount / notifs.length * 100), trendDir: 'neutral', trendColor: '#F59E0B' },
    { label: 'Drafts', value: draftCount,variant: 'cyan', icon: Pencil,  color: '#06B6D4', colorGlow: 'rgba(6,182,212,0.15)', trend: `${draftCount}`, bar: Math.round(draftCount / notifs.length * 100), trendDir: 'neutral', trendColor: '#06B6D4' },
    { label: 'Total Campaigns', value: notifs.length,variant: 'green', icon: LayoutList,  color: '#10B981', colorGlow: 'rgba(16,185,129,0.15)', trend: `${notifs.length}`, bar: 100 ,trendColor: '#10B981'},
  ]

  return (
    <div className="dash-page page-in">
      <section className="notif-hero card">
        <div className="notif-hero-glow" />
        <div>
          <p className="notif-overline">Campaign Center</p>
          <h1 className="page-title" style={{ marginBottom: 4 }}>Push Notifications</h1>
          <p style={{ color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>Create and monitor targeted broadcast campaigns.</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> New Notification</button>
      </section>

      <div className="stats-grid cols-4">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{ "position": "absolute", "top": "0px", "right": "0px", "width": "110px", "height": "110px", "border-radius": "50%", "background": s.colorGlow, "filter": "blur(30px)", "transform": "translate(30%, -30%)", "pointer-events": "none" }}></span>
             <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.colorGlow, color: s.color, border: `1px solid ${s.colorGlow}` }}>
                <s.icon size={15} strokeWidth={1.8} />
              </div>
              <span className={`stat-card-trend${s.trendDir === 'neutral' ? ' neutral' : ''}`} style={{ color: s.trendColor, background: `${s.trendColor}15`, border: `1px solid ${s.colorGlow}` }}>
                {s.trend}%
              </span>
             </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ "position": "absolute", "bottom": "0px", "left": "0px", "right": "0px", "height": "2px", background: `linear-gradient(90deg,${s.color}88,transparent)`, "border-radius": "0 0 var(--radius-lg) var(--radius-lg)" }}></div>
          </div>
        ))}
      </div>
           

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text1)' }}>Notification History</span>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr><th>ID</th><th>Title</th><th>Audience</th><th>Reach</th><th>Status</th><th>Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {notifs.map(n => (
                <tr key={n.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)', color: 'var(--accent-hover)' }}>{n.id}</td>
                  <td style={{ fontWeight: 500, color: 'var(--text1)' }}>{n.title}</td>
                  <td>{n.audience}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{n.reach}</td>
                  <td><span className={`badge ${STATUS[n.status].cls}`}>{STATUS[n.status].l}</span></td>
                  <td style={{ color: 'var(--text3)', fontSize: 'var(--fs-xs)' }}>{n.sent}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-icon" onClick={() => openEdit(n)} aria-label="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-ghost btn-icon" onClick={() => { setActive(n); setDeleteOpen(true) }} aria-label="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={active ? 'Edit Notification' : 'Compose Notification'}
        subtitle="Create campaign and target audience"
        onClose={() => setModalOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
            <button className="btn btn-ghost" onClick={() => setForm({ ...form, status: 'draft' })}>Save as Draft</button>
            <button className="btn btn-primary" onClick={save}><Send size={14} /> {active ? 'Update' : 'Send / Save'}</button>
          </>
        )}
      >
        <div className="form-grid cols-2">
          <div className="form-field">
            <label>Notification Title</label>
            <input className="input" placeholder="Enter title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="form-field">
            <label>Target Audience</label>
            <select className="input" value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })}>
              <option>All Users</option><option>Pro Users</option><option>Enterprise</option><option>New Users</option>
            </select>
          </div>
          <div className="form-field">
            <label>Campaign Status</label>
            <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="sent">sent</option>
              <option value="scheduled">scheduled</option>
              <option value="draft">draft</option>
            </select>
          </div>
          <div className="form-field">
            <label>Expected Reach</label>
            <input className="input" placeholder="e.g. 24,000" value={form.reach} onChange={e => setForm({ ...form, reach: e.target.value })} />
          </div>
          <div className="form-field full">
            <label>Message Body</label>
            <textarea className="input" rows={4} placeholder="Write campaign message..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} style={{ resize: 'vertical' }} />
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={deleteOpen}
        title="Delete notification"
        message={`Delete ${active?.title || 'selected notification'}?`}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onDelete}
      />

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        .notif-hero {
          position:relative; display:flex; justify-content:space-between;
          align-items:flex-end; gap:var(--space-md); flex-wrap:wrap;
          background: radial-gradient(120% 150% at 0% 0%, rgba(79,70,229,.22), rgba(6,182,212,.08));
          border-color: var(--border-active);
        }
        .notif-overline { font-size:var(--fs-xs); text-transform:uppercase; letter-spacing:.08em; color:var(--accent-hover); font-weight:700; margin-bottom:4px; }
        .notif-hero-glow { position:absolute; width:220px; height:220px; right:-70px; top:-70px; border-radius:50%; background:rgba(6,182,212,.16); filter:blur(40px); pointer-events:none; }
      `}</style>
    </div>
  )
}